import { Box, Cards, Tabs } from '@amzn/awsui-components-react';
import React, { useContext, useEffect, useState } from 'react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { createCardDefinition } from './DashboardItem';
import { CourseProps, waitlistedCourses, availableCourses, enrolledCourses, pastCourses, courseClassifier, Tab } from '../../common';
import styled from 'styled-components';
import { CourseContext } from '../../components/course/CourseContext';
import { useTrackUserEvent } from '../../common/telemetry';
import { TelemetryEvents, TELEMETRY_EVENT_TYPES } from '../../common/TelemetryEvents';

// Utility functions for tab visibility and width calculations
interface TabConfig {
    id: Tab;
    label: React.ReactNode;
    disabled: boolean;
}

/**
 * Filters tabs based on waitlisted course availability
 * @param courses Array of course props to check for waitlisted courses
 * @returns Array of visible tab configurations
 */
export const getVisibleTabs = (courses: CourseProps[]): TabConfig[] => {
    const baseTabs: TabConfig[] = [
        {
            id: Tab.Available,
            label: (
                <h2>
                    Available classes
                    <p>{availableCourses(courses).length}</p>
                </h2>
            ),
            disabled: availableCourses(courses).length === 0,
        },
        {
            id: Tab.Enrolled,
            label: (
                <h2>
                    Enrolled classes
                    <p>{enrolledCourses(courses).length}</p>
                </h2>
            ),
            disabled: enrolledCourses(courses).length === 0,
        },
        {
            id: Tab.Past,
            label: (
                <h2>
                    Past classes
                    <p>{pastCourses(courses).length}</p>
                </h2>
            ),
            disabled: pastCourses(courses).length === 0,
        },
    ];

    // Insert waitlisted tab between Available and Enrolled if there are waitlisted courses
    if (waitlistedCourses(courses).length > 0) {
        baseTabs.splice(1, 0, {
            id: Tab.Waitlisted,
            label: (
                <h2>
                    Waitlisted classes
                    <p>{waitlistedCourses(courses).length}</p>
                </h2>
            ),
            disabled: waitlistedCourses(courses).length === 0,
        });
    }

    return baseTabs;
};

/**
 * Calculates percentage width based on tab count using floor for decimal precision with slight reduction to prevent scroll arrows
 * @param tabCount Number of visible tabs
 * @returns CSS width percentage as string
 */
export const getTabWidth = (tabCount: number): string => {
    // Special cases for common scenarios
    if (tabCount === 3) return '33.2%';
    if (tabCount === 4) return '24.9%';

    // Dynamic calculation for other tab counts
    const percentage = 100 / tabCount;
    return `${Math.floor(percentage * 100) / 100}%`;
};

const StyledTabs = styled(Tabs)<{ tabCount: number }>`
    h2 {
        font-size: x-large;
        text-align: center;
        width: 100%;
        margin: -30px 0 0 0;
    }

    @media screen and (max-width: 768px) {
        h2 {
            font-size: 16px;
        }
    }

    h2 p {
        font-size: 48px;
        font-weight: 900;
        margin: 30px 0 0 0;
    }

    li[role='presentation'] {
        width: ${(props) => getTabWidth(props.tabCount)} !important;
        max-width: ${(props) => getTabWidth(props.tabCount)} !important;
        min-width: ${(props) => getTabWidth(props.tabCount)} !important;
        flex: 0 0 ${(props) => getTabWidth(props.tabCount)} !important;
        height: 120px;
        transition: width 0.3s ease-in-out;
    }

    li[role='presentation'] div {
        width: 100%;
    }

    li[role='presentation'] button {
        width: 100%;
    }

    li[role='presentation'] button span {
        width: 100%;
    }

    div[role='tabpanel'] {
        display: none !important;
    }

    @media screen and (max-width: 768px) {
        li[role='presentation'] {
            width: ${(props) => getTabWidth(props.tabCount)} !important;
            max-width: ${(props) => getTabWidth(props.tabCount)} !important;
            min-width: ${(props) => getTabWidth(props.tabCount)} !important;
            flex: 0 0 ${(props) => getTabWidth(props.tabCount)} !important;
        }
    }
`;

export const DashboardListWithWaitlistedTab: React.FC = () => {
    const { loading, courses } = useContext(CourseContext);
    const [activeTab, setActiveTab] = useState<Tab>(Tab.Available);
    const trackUserEvent = useTrackUserEvent();
    const [previousWaitlistedCount, setPreviousWaitlistedCount] = useState<number>(0);

    // Get visible tabs based on course availability
    const visibleTabs = React.useMemo(() => getVisibleTabs(courses), [courses]);

    const { items, collectionProps } = useCollection(courses, {
        filtering: {
            filteringFunction: (item: CourseProps) => {
                return courseClassifier(item) === activeTab;
            },
        },
    });

    useEffect(() => {
        // Whenever the learner registers or withdraws, check if we should switch tabs because the current tab might be empty.
        if (!courses.length) return;

        let currentTab = activeTab;
        const currentWaitlistedCount = waitlistedCourses(courses).length;
        const isWaitlistedTabVisible = visibleTabs.some((tab) => tab.id === Tab.Waitlisted);

        // Check if current active tab is still visible in the tab list
        const isCurrentTabVisible = visibleTabs.some((tab) => tab.id === currentTab);

        // Auto-switch to waitlisted tab when a course is newly waitlisted
        // This happens when waitlisted count increases and the waitlisted tab becomes visible
        if (currentWaitlistedCount > previousWaitlistedCount && isWaitlistedTabVisible && currentTab !== Tab.Waitlisted) {
            currentTab = Tab.Waitlisted;
        }

        // If current tab is waitlisted but waitlisted tab is not visible (no waitlisted courses), switch away
        if (currentTab === Tab.Waitlisted && !isWaitlistedTabVisible) {
            currentTab = Tab.Available;
        }

        // If current tab is not visible in the tab list, switch to a fallback tab
        if (!isCurrentTabVisible) {
            // Prioritize tabs in order: Available -> Enrolled -> Past
            if (availableCourses(courses).length > 0) {
                currentTab = Tab.Available;
            } else if (enrolledCourses(courses).length > 0) {
                currentTab = Tab.Enrolled;
            } else {
                currentTab = Tab.Past;
            }
        }

        // If the active tab is available classes but there are none, then set it to enrolled classes instead
        if (currentTab === Tab.Available && availableCourses(courses).length === 0) {
            currentTab = Tab.Enrolled;
        }

        // If active tab is enrolled classes but there are none, then set it to available classes unless there are none, in which case set it to past.
        if (currentTab === Tab.Enrolled && enrolledCourses(courses).length === 0) {
            currentTab = availableCourses(courses).length > 0 ? Tab.Available : Tab.Past;
        }

        // Update previous waitlisted count for next comparison
        setPreviousWaitlistedCount(currentWaitlistedCount);

        setActiveTab(currentTab);
    }, [courses, activeTab, visibleTabs, previousWaitlistedCount]);

    return (
        <Cards
            {...collectionProps}
            cardDefinition={createCardDefinition()}
            cardsPerRow={[
                { cards: 1 },
                {
                    minWidth: 768,
                    cards: 2,
                },
            ]}
            loading={loading}
            loadingText='Loading classes'
            empty={
                <Box margin={{ vertical: 'xs' }} textAlign='center' color='inherit'>
                    <b>No classes found</b>
                </Box>
            }
            items={items}
            filter={
                <StyledTabs
                    tabCount={visibleTabs.length}
                    tabs={visibleTabs}
                    onChange={({ detail }) => {
                        trackUserEvent(
                            TELEMETRY_EVENT_TYPES.CLICK,
                            `${TelemetryEvents.DASHBOARD_TAB_CHANGE}|${detail.activeTabId.toLowerCase()}`,
                        );
                        setActiveTab(detail.activeTabId as Tab);
                    }}
                    activeTabId={activeTab}
                />
            }
        />
    );
};
